import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AgGridAngular } from 'ag-grid-angular';
import { IServerSideDatasource, IServerSideGetRowsParams, GridApi, ColDef, GridState, GridReadyEvent, ValueGetterParams, ColumnPanelItemDragEndEvent, DateStringAdvancedFilterModel } from 'ag-grid-community';
import { FormBuilder, FormGroup, FormArray, ValidatorFn, Validators, ValidationErrors, AbstractControl, FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, switchMap, map} from 'rxjs/operators';
import { ContactAutocompleteService, ContactAutocompleteResult, ContactDetails} from '../../services/contacts/contact-autocomplete.service';
import { IAddressOption, IFreightmancerPayload, IRaterPallet, IRaterArgument } from '../../freightmancerlvl99/freightmancerlvl99.models';
import { ArrayDataSource } from '@angular/cdk/collections';

/*
THIS PAGE WAS MODIFIED ("VERSION 2") TO GET ZONE RATES FOR HYPOTHETICAL SHIPMENTS
UI IS UGLY BUT IT'S BEEN TESTED 2026-02-10
MIGHT WANT TO MAKE THE POSTAL CODE VALIDATOR MORE FLEXIBLE. ALLOW LOWER CASE.
BUT IF YOU DO THAT, FIX THE POSTAL CODE FORMATTING BEFORE SENDING TO AUTORATER

ALSO AUTORATER CURRENTLY DOES NOT SEND TRANSIT DAYS FOR CUSTOMER BILLABLE
*/

@Component({
  selector: 'app-get-rates',
  templateUrl: './get-rates.component.html',
  styleUrls: ['./get-rates.component.scss']
})
export class GetRatesComponent {
  // ------- VERSION 2 STUFF ----------
  searchForm: FormGroup = this.fb.group({
    customerContactUuid: ['', Validators.required],
    customerContactName: ['', Validators.required],
    notExistingCustomer: [false],
    originContactUuid: '',
    originAddressUuid: '',
    originPostalCode: ['', postalCodeValidator()],
    originContactName: '',
    destinationContactUuid: '',
    destinationAddressUuid: '',
    destinationPostalCode: ['', postalCodeValidator()],
    destinationCity: [''],
    destinationRegion: [''],
    destinationContactName: '',
    load: this.fb.array([]),
    customerProfileUuid: [''],
    customerProfileName: ['']
  },
  {
    validators: [originValidator(), destinationValidator()]
  });
  customerBillableForm: FormGroup = this.fb.group({
    billable_rate: [0.0],
    transit_days: [0]
  });

  // Address lists
  customerAddresses: IAddressOption[] = [];
  shipperAddresses: IAddressOption[] = [];
  consigneeAddresses: IAddressOption[] = [];

  customerProfiles: any[] = [];

  // Load stuff
  palletForm: FormGroup = this.fb.group({
    number: [0, [positiveIntegerValidator()]],
    width: [0.0, [Validators.min(0)]],
    length: [0.0, [Validators.min(0)]],
    height: [0.0, [Validators.min(0)]],
    weight: [0.0, [Validators.min(0)]]
  }, {
    validators: [palletDimensionsValidator()]
  });
  addPalletProduct(){
    this.load.push(this.palletForm);
  }
  removePalletProduct(i: number): void {
    this.load.removeAt(i);
  }

  // Getters
  get load(){
    return this.searchForm.controls['load'] as FormArray;
  }
  get shipperNameControl(): FormControl {
    return this.searchForm.get('originContactName') as FormControl;
  }
  get consigneeNameControl(): FormControl {
    return this.searchForm.get('destinationContactName') as FormControl;
  }
  get customerNameControl(): FormControl {
    return this.searchForm.get('customerContactName') as FormControl;
  }

  // Results Grid
  private gridApi!: GridApi;
  rowData: any[] = [];
  columnDefs: ColDef[] = [
    { field: 'carrier_name', headerName: 'Carrier Name'},
    { field: 'rate_method', headerName: 'Rate Method'},
    { field: 'rate_unit', headerName: 'Unit'},
    { field: 'total_rate', headerName: 'Total Rate', sortable: true}
  ];
  defaultColDef: ColDef = {
    resizable: true,
    sortable: false
  }

  // Historic Market Rates Grid
  historicMarketRowData: any[] = [];
  historicMarketColDefs: ColDef[] = [
    { field: 'carrier_name', headerName: 'Carrier Name'},
    { field: 'lading_id', headerName: 'Lading ID'},
    { field: 'origin', headerName: 'Origin', valueGetter: (params) => {
      return `${params.data.origin_city}, ${params.data.origin_state}`;
    }},
    { field: 'destination', headerName: 'Destination', valueGetter: (params) => {
      return `${params.data.dest_city}, ${params.data.dest_state}`;
    }},
    { field: 'freight_net_billable', headerName: 'Freight Billable', sortable: true},
    { field: 'gross_costs', headerName: 'Gross Costs', sortable: true},
    { field: 'total_charged', headerName: 'Total Charged', sortable: true}
  ];

  // Contact selection
  onCustomerSelected(opt: ContactAutocompleteResult): void {
    this.searchForm.patchValue({
      customerContactUuid: opt.value,
      customerContactName: opt.label
    });

    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.customerProfiles = (data.profile || []).map(profile => ({
        value: profile.contact_profile_uuid,
        label: profile.profile_name
      }));

      if(this.customerProfiles.length){
        this.searchForm.get('customerProfileUuid')!.setValue(this.customerProfiles[0].value);
      }
    })
  }
  onShipperSelected(opt: ContactAutocompleteResult): void {
    this.searchForm.patchValue({
      originContactUuid: opt.value,
      originContactName: opt.label
    })

    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.shipperAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', ')
      }));

      if(this.shipperAddresses.length){
        this.searchForm.get('originAddressUuid')!.setValue(this.shipperAddresses[0].value);
      }
      this.searchForm.get('originPostalCode')!.setValue(data.addresses?.[0]?.postal_code || '');


    })
  }
  onConsigneeSelected(opt: ContactAutocompleteResult): void {
    this.searchForm.patchValue({
      destinationContactUuid: opt.value,
      destinationContactName: opt.label
    })

    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.consigneeAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', ')
      }));

      if(this.consigneeAddresses.length){
        this.searchForm.get('destinationAddressUuid')!.setValue(this.consigneeAddresses[0].value);
      }
      this.searchForm.get('destinationPostalCode')!.setValue(data.addresses?.[0]?.postal_code || '');
    })
  }

  searchRates(){
    //console.log(this.searchForm.controls);
    // run validators and show errors in UI
    this.searchForm.markAllAsTouched();
    this.searchForm.updateValueAndValidity({ onlySelf: false, emitEvent: false});

    const errors = this.collectAllErrors(this.searchForm);

    if(errors.length > 0){
      this.validationErrors = errors;
      console.warn('Validation errors:', errors);
      return;
    }

    let originType: 'BY_POSTAL_CODE' | 'CITY_STATE' | 'BY_ZONE_UUID';
    let originVal: string;
    let destinationType: 'BY_POSTAL_CODE' | 'CITY_STATE' | 'BY_ZONE_UUID';
    let destinationVal: string;
    const payload = this.toSnakeCaseDeep(this.searchForm.getRawValue());
    delete payload.customer_contact_name;
    delete payload.origin_contact_name;
    delete payload.destination_contact_name;
    payload['shipment'] = { pallets: payload['load'] };
    delete payload.load;
    if(payload.origin_address_uuid !== null && payload.destination_address_uuid !== ''){
      originType = 'BY_ZONE_UUID';
      originVal =  payload.origin_address_uuid;
    }
    else{
      originType = 'BY_POSTAL_CODE';
      originVal = payload.origin_postal_code.toUpperCase();
    }
    if(payload.destination_address_uuid !== null && payload.destination_address_uuid !== ''){
      console.log(payload.destination_address_uuid)
      destinationType = 'BY_ZONE_UUID';
      destinationVal = payload.destination_address_uuid;
    }
    else{
      destinationType = 'BY_POSTAL_CODE';
      destinationVal = payload.destination_postal_code.toUpperCase();
    }
    payload['locations'] = {
      source: {
        type: originType,
        value: originVal
      },
      destination: {
        type: destinationType,
        value: destinationVal
      }
    };
    delete payload.origin;
    delete payload.destination;
    payload['debug'] = true;
    console.log(payload)

    // call the autorater
    const url = '/api/rates/freightmancer_rates.php';
    this.http.post<AutoraterResponse>(url, payload).subscribe(
      res => {
        console.log(res);
        if(res['carrier_rates']){
          res['carrier_rates'].forEach((item: CarrierRate) => {
              // Turn rate method into string
              const rateMethodMapping: Record<number, string> = {
                1: 'WEIGHT',
                2: 'LENGTH',
                3: 'CUBIC WEIGHT',
                4: 'WEIGHT PER CUBIC FOOT'
              }
              if(item['rate_method'] !== null){
                let methodKey = Number(item['rate_method']);
                (item as any).rate_method = rateMethodMapping[methodKey] ?? item.rate_method;
              }

              // Turn rate unit into string
              const rateUnitMapping: Record<number, string> = {
                1: 'POUNDS',
                2: 'KILOGRAMS',
                3: 'FEET',
                4: 'METRES',
                5: 'PER 100 POUNDS'
              };
              if(item['rate_unit'] !== null){
                let unitKey = Number(item['rate_unit']);
                (item as any).rate_unit = rateUnitMapping[unitKey] ?? item.rate_unit;
              }

              // Convert total rate into dollars
              if(item['total_rate']){
                item['total_rate'] /= 100.00;
              }
          });
          this.rowData = res['carrier_rates'];
          this.historicMarketRowData = res['historic_rates_market'] ?? [];
        }
        if(res['customer_billable']){
          this.customerBillableForm.patchValue({
            transit_days: res.customer_billable.transit_days ?? null,
            billable_rate: res.customer_billable.total_rate / 100.00
          })
        }
      },
      err => {
        console.error(err);
      }
    )
  }

  validationErrors: Array<{ path: string; key: string; details?: any }> = [];
  private collectAllErrors(control: AbstractControl, path = ''): Array<{ path: string; key: string; details?: any }> {
    const out: Array<{ path: string; key: string; details?: any }> = [];

    // Control-level errors (includes FormGroup/FormArray errors too)
    if (control.errors) {
      for (const [key, details] of Object.entries(control.errors)) {
        out.push({ path: path || '(form)', key, details });
      }
    }

    if (control instanceof FormGroup) {
      for (const [name, child] of Object.entries(control.controls)) {
        const childPath = path ? `${path}.${name}` : name;
        out.push(...this.collectAllErrors(child, childPath));
      }
    } else if (control instanceof FormArray) {
      control.controls.forEach((child, i) => {
        const childPath = `${path}[${i}]`;
        out.push(...this.collectAllErrors(child, childPath));
      });
    }

    return out;
  }
  private toSnakeCaseDeep(value: any): any {
    if (Array.isArray(value)) {
      return value.map(v => this.toSnakeCaseDeep(v));
    }

    if (value !== null && typeof value === 'object') {
      const out: any = {};
      for (const [k, v] of Object.entries(value)) {
        out[this.camelToSnake(k)] = this.toSnakeCaseDeep(v);
      }
      return out;
    }

    return value;
  }

  private camelToSnake(s: string): string {
    return s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  }

  private fixPostalCode(postalCode: string){

  }

  resetSearchForm(){
    this.searchForm.reset();
  }

  // ------------- END OF VERSION 2 STUFF ---------------

  customerRate: any = null;

  form = this.fb.group({
    origin_type: ['', Validators.required],
    destination_type: ['', Validators.required],
    origin_city: [''],
    destination_city: [''],
    origin_region: [''],
    destination_region: [''],
    origin_postal: [''],
    destination_postal: [''],
    rate_metric: ['', Validators.required],
    rate_unit: ['', Validators.required],
    measurement: [0, Validators.required],
    customer_uuid: ['b0c472a0-e85a-439a-9dbc-2ae40304a9f5'],
    customer_uuid_2: [''],
    customer_name: [''],
    carrier_uuid: [''],
    carrier_name: [''],
    demo_carrier_uuid: ['4354d4bc-7a69-4d7b-b29b-16e62a56f2ee']
  },
  { validators: customOriginValidator}
  )
  filteredCustomerList: any[] = [];
  filteredCarrierList: any[] = [];

  results_form = this.fb.group({
    customerRate: [0],
    transit_days: [0],
    carrierRate: [0],
    default_carrier: [''],
    default_customer: ['']
  })

  rate_metrics = [
    'Weight',
    'Length'
  ]

  rate_units = [
    'LBS',
    'FT'
  ]

  location_types = [
    'CITY_STATE',
    'POSTAL_CODE'
  ]

  constructor(private http: HttpClient, private fb: FormBuilder, private contactSvc: ContactAutocompleteService){}

  search(){
    if(this.form.get('origin_city')?.value !== ''){
      this.form.get('origin_type')?.setValue('CITY_STATE');
    }
    else{
      this.form.get('origin_type')?.setValue('POSTAL_CODE');
    }

    if(this.form.get('destination_city')?.value !== ''){
      this.form.get('destination_type')?.setValue('CITY_STATE');
    }
    else{
      this.form.get('destination_type')?.setValue('POSTAL_CODE');
    }


    let origin_location = this.form.get('origin_type')?.value === 'CITY_STATE' ? this.form.get('origin_city')?.value + ', ' + this.form.get('origin_region')?.value : this.form.get('origin_postal')?.value;
    let destination_location = this.form.get('destination_type')?.value === 'CITY_STATE' ? this.form.get('destination_city')?.value + ', ' + this.form.get('destination_region')?.value : this.form.get('destination_postal')?.value;
    let postBody = {
      rate_method: this.form.get('rate_metric')?.value,
      customer_contact_uuid: this.form.get('customer_uuid')?.value,
      carrier_contat_uuid: this.form.get('demo_carrier_uuid')?.value,
      zone_data: false,
      measurement: this.form.get('measurement')?.value,
      locations: {
        origin: {
          type: this.form.get('origin_type')?.value,
          value: origin_location
        },
        destination: {
          type: this.form.get('destination_type')?.value,
          value: destination_location
        }
      }
    }
    console.log('Post body: ', JSON.stringify(postBody));
    this.http.post<any>(`api/rates/get_rates.php`, postBody).subscribe({
      next: (response) => {
        if(!response){
          alert('No response from server or response is null');
          return;
        }
        // Customer rate data
        let min_rate = +response.customer_rate.min_rate;
        let total_rate = +response.customer_rate.total_rate;
        let val_to_set = min_rate > total_rate ? min_rate : total_rate;
        this.results_form.get('customerRate')?.setValue(val_to_set);
        this.results_form.get('transit_days')?.setValue(response.customer_rate.transit_days);
        this.results_form.get('default_customer')?.setValue(
          response.is_customer_default ? 'Market Rate' : 'Customer Rate'
        );
        // Carrier rate data
        min_rate = +response.carrier_rate.min_rate;
        total_rate = +response.carrier_rate.total_rate;
        val_to_set = min_rate > total_rate ? min_rate : total_rate;
        this.results_form.get('carrierRate')?.setValue(val_to_set);
        this.results_form.get('default_carrier')?.setValue(
          response.is_carrier_default ? 'Market Rate' : 'Customer Rate'
        );
        // if (response.status !== "200") { 
        //   alert('No rate was found with the given data.');
        // }
      },
      error: (error) => {
        console.error(error);
        alert('Error processing data.');
      }

    })
    
  }

  resetForm(){
    this.form.reset();
    this.results_form.reset();
  }

  ngOnInit(){
    this.form.get('customer_name')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        const searchTerm = value ?? '';
        return searchTerm.length >= 3 ? this.searchContacts(searchTerm) : [];
      })
    )
    .subscribe(options => {
      this.filteredCustomerList = options
      console.log('Filtered customer list updated...' , JSON.stringify(options))
    })
    this.form.get('carrier_name')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        const searchTerm = value ?? '';
        return searchTerm.length >= 3 ? this.searchContacts(searchTerm) : [];
      })
    )
    .subscribe( options => {
      this.filteredCarrierList = options
    })

    // ---------------- VERSION 2 STUFF BELOW ---------------
    // change whether customer is required depending on whether user marked that customer doesn't exist
    const customerUuid = this.searchForm.get('customerContactUuid');
    const customerName = this.searchForm.get('customerContactName');
    const notExistingCustomer = this.searchForm.get('notExistingCustomer');
    notExistingCustomer?.valueChanges.subscribe((isTrue: boolean) => {
      if (isTrue){
        customerUuid?.removeValidators(Validators.required);
        customerName?.removeValidators(Validators.required);
      }
      else{
        customerUuid?.addValidators(Validators.required);
        customerName?.addValidators(Validators.required);
      }
      customerUuid?.updateValueAndValidity();
      customerName?.updateValueAndValidity();
    })
  }

  searchContacts(query: string){
    let url = `/api/autocomplete/autocomplete.php?query=${query}`;
    return this.http.get<any>(url).pipe(map(response => response.data))
  }

  onOptionSelected(event: any, contact_type: string): void{
    console.log('Event sent to onOptionSelected: ', event)
    if(contact_type === 'customer'){
      const selectedOption = this.filteredCustomerList.find(option => option.contact_organization === event.option.value);
      if (selectedOption){
        this.form.get('customer_uuid_2')?.setValue(selectedOption.contact_uuid);
      }
    }
    else if(contact_type === 'carrier'){
      const selectedOption = this.filteredCarrierList.find(option => option.contact_organization === event.option.value);
      if (selectedOption){
        this.form.get('carrier_uuid')?.setValue(selectedOption.contact_uuid);
      }
    }
  }

  onGridReady(params: GridReadyEvent){
    this.rowData = [];
    this.historicMarketRowData = [];
  }

  dateToString(date: null | Date | string): string{
    if (!date) {
      return 'Date not set';
    }
    const newDate = (typeof date === 'string') ? new Date(date) : date;
    if (isNaN(newDate.getTime())) {
      return 'Invalid Date';
    }
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const weekday = weekdays[newDate.getDay()];
    const month = months[newDate.getMonth()];
    return `${weekday} ${month} ${newDate.getDate()} ${newDate.getFullYear()}`;
  }

}

export function   customOriginValidator(form: FormGroup) {
  const city = form.get('origin_city')?.value;
  const region = form.get('origin_region')?.value;
  const postalCode = form.get('origin_postal')?.value;

  if (city && !region) {
    form.get('origin_region')?.setErrors({ required: true });
  } else if (region && !city) {
    form.get('origin_city')?.setErrors({ required: true });
  } else if (!city && !region && !postalCode) {
    form.get('origin_postal')?.setErrors({ required: true });
  } else {
    form.get('origin_city')?.setErrors(null);
    form.get('origin_region')?.setErrors(null);
    form.get('origin_postal')?.setErrors(null);
  }

  return null;
}

export function   customDestinationValidator(form: FormGroup) {
  const city = form.get('destination_city')?.value;
  const region = form.get('destination_region')?.value;
  const postalCode = form.get('destination_postal')?.value;

  if (city && !region) {
    form.get('destination_region')?.setErrors({ required: true });
  } else if (region && !city) {
    form.get('destination_city')?.setErrors({ required: true });
  } else if (!city && !region && !postalCode) {
    form.get('destination_postal')?.setErrors({ required: true });
  } else {
    form.get('destination_region')?.setErrors(null);
    form.get('destination_city')?.setErrors(null);
    form.get('destination_postal')?.setErrors(null);
  }

  return null;
}

// form group validator for origin
export function originValidator(): ValidatorFn{
  // rookie dev note: a ValidatorFn must have an AbstractControl as its parameter
  return (groupControl: AbstractControl): ValidationErrors | null => {
    const group = groupControl as FormGroup;
    const origin_address_uuid = group.get('origin_address_uuid')?.value;
    const origin_postal_code = group.get('origin_postal_code')?.value;

    if(origin_postal_code === '' && origin_address_uuid === ''){
      return { originAddressNotSet: true };
    }
    else{
      return null;
    }
  }
}

// form group validator for destination
export function destinationValidator(): ValidatorFn{
  return (groupControl: AbstractControl): ValidationErrors | null => {
    const group = groupControl as FormGroup;
    const destination_address_uuid = group.get('destination_address_uuid')?.value;
    const destination_postal_code = group.get('destination_postal_code')?.value;

    if(destination_address_uuid === '' && destination_postal_code === ''){
      return { destinationAddressNotSet: true };
    }
    else{
      return null;
    }
  }
}

export function palletDimensionsValidator(): ValidatorFn{
  return (groupControl: AbstractControl): ValidationErrors | null => {
    const group = groupControl as FormGroup;
    const num_of_pallets = group.get('number')?.value ?? 0;
    const pallet_length = group.get('length')?.value ?? 0;
    const pallet_width = group.get('width')?.value ?? 0;
    const pallet_height = group.get('height')?.value ?? 0;

    if(num_of_pallets == 0 && (pallet_length == 0 || pallet_width == 0 || pallet_height == 0)){
      return { palletOrDimsNotSet: true };
    }
    else{
      return null;
    }
  }
}

// form control validator for postal codes
export function postalCodeValidator(): ValidatorFn{
  return (control: AbstractControl): ValidationErrors | null => {
    const postalCode = control.value;
    const postalCodeRegex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/;
    const zipCodeRegex = /^\d{5}(-\d{4})?$/;
    // it's fine if postal code is not set
    if (postalCode == null || postalCode === ''){
      return null;
    }
    // it's fine if postal code fits patterns for canadian or american codes
    if (postalCodeRegex.test(postalCode) || zipCodeRegex.test(postalCode)){
      return null;
    }
    // it's not fine if postal code is set but it doesn't fit the patterns
    return { invalidPostalCode: true }
  }
}

// generic form control validator for checking that a control's value is a positive
export function positiveIntegerValidator(): ValidatorFn{
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') return null;

    const  n = Number(value);
    if (!Number.isInteger(n) || n < 0) {
      return { negativeOrInteger: true}
    }
    return null;
  }
}

type RateMethod = 1 | 2 | 3 | 4;
type RateUnit = 1 | 2 | 3 | 4 | 5;

export interface CarrierRate {
  carrier_name: string,
  carrier_contact_uuid: string,
  rate_method: RateMethod | string,
  rate_unit: RateUnit | string,
  total_rate: number
}

export interface CustomerBillable {
  min_rate: number | null,
  rate: number | null,
  rate_method: RateMethod | string,
  rate_unit: RateUnit | string,
  total_rate:  number,
  rate_uuid: string | null,
  transit_days?: number | null
}

export interface AutoraterResponse {
  customer_billable: CustomerBillable,
  carrier_rates: CarrierRate[],
  debug_log: string[],
  fsc_default: number | null,
  tax_rate: number | null,
  historic_rates_market: any[]
}